import { GroupWrapper } from '@dealstack/frontend/shared/components/fundament/GroupWrapper/GroupWrapper';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { RegisterOptions, useFormContext, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import { IGroupWrapperProps } from '@dealstack/frontend/shared/components/fundament/GroupWrapper/GroupWrapper.types';

export interface Props extends IGroupWrapperProps {
	className?: string;
	name: string;
	rows: number;
	cols?: number;
	rhfOptions?: RegisterOptions;
	disabled?: boolean;
	resizable?: boolean;
	fullWidth?: boolean;
	autoFocus?: boolean;
	'data-testid'?: string;
	placeholder?: string;
}

const TextArea = ({
	className,
	name,
	hint,
	label,
	disabled,
	rows,
	tooltip,
	cols,
	fullWidth,
	rhfOptions,
	autoFocus,
	'data-testid': dataTestId,
	placeholder,
}: Props) => {
	const methods = useFormContext();
	const fieldApi = methods.register(name, rhfOptions);
	const tCommonUi = useTranslationCommonUi();

	useWatch();
	const errors = methods.formState.errors[name];
	return (
		<div className={className}>
			<GroupWrapper label={label} hint={hint} tooltip={tooltip} errors={errors}>
				<InputWrapper fullWidth={fullWidth} isError={!!errors}>
					<textarea
						data-testid={dataTestId ?? `input-string-${name}`}
						disabled={disabled}
						placeholder={placeholder ?? tCommonUi('textarea.placeholder')}
						rows={rows}
						cols={cols}
						autoFocus={autoFocus}
						{...fieldApi}
					/>
				</InputWrapper>
			</GroupWrapper>
		</div>
	);
};
const InputWrapper = styled.div<{
	isError?: boolean;
	fullWidth?: boolean;
}>`
	position: relative;
	width: ${({ fullWidth }) => (fullWidth ? '100%' : 'fit-content')};

	${({ theme, isError }) =>
		isError
			? `
        textarea {
            border-color: ${theme.v2.color.error[600]} !important;
        }
    `
			: ``}
`;

const Styled = styled(TextArea)`
	display: flex;
	flex-direction: column;

	--placeholder-color: ${({ theme }) => theme.v2.color.neutral[500]};
	--text-color: ${({ theme }) => theme.v2.color.neutral[1100]};
	--border-color: ${({ theme }) => theme.v2.color.neutral[500]};
	--border-color-focused: ${({ theme }) => theme.v2.color.neutral[700]};

	textarea {
		border: 1px solid var(--border-color);
		border-radius: 4px;
		padding: 0.6rem;
		font-size: 0.75rem;
		box-sizing: border-box;

		${({ theme, disabled }) =>
			disabled
				? `
                --text-color: ${theme.v2.color.neutral[500]};
                --border-color: ${theme.v2.color.neutral[400]};
                background-color: ${theme.v2.color.neutral[200]};
        `
				: ``}

		resize: none;
		${({ resizable }) =>
			resizable
				? `
                    resize: auto;
                `
				: ``}

		${({ fullWidth }) => (fullWidth ? 'width: 100%' : '')};

		&:focus {
			border: 1px solid var(--border-color-focused);
		}
		caret-color: var(--text-color);
		color: var(--text-color);

		&::placeholder {
			color: var(--placeholder-color);
			opacity: 1; /* Firefox */
		}
	}

	${({ disabled }) =>
		disabled
			? `
            ${InputWrapper} {
                * {
                cursor: not-allowed !important;
                }
            }
    `
			: ``}
`;

export { Styled as TextArea };
